import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Grid2X2,
  List,
  BookOpen,
  FileText,
  FileBadge,
  Newspaper,
  FlaskConical,
  Dumbbell,
  Clock3,
  Star,
  User,
  Bookmark,
  ChevronRight,
  Eye,
  BookCopy,
  ClipboardList,
  Leaf,
  Activity,
  Heart,
  Video,
  File,
  FileCheck,
  ListChecks,
  FileSearch,
  FileSpreadsheet,
  Image,
  FileQuestion,
} from "lucide-react";
import { getMyResourcesLibrary } from "../../api/api";
import ResourceDetail from "./ResourceDetail";

export default function DigitalLibrary() {
  const [featuredResources, setFeaturedResources] = useState([]);
  const [allResources, setAllResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [trending, setTrending] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [topics, setTopics] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedSort, setSelectedSort] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResources, setTotalResources] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [activeFilter, setActiveFilter] = useState("All");

  const [selectedResource, setSelectedResource] = useState(null);
  const [isResourceDetailsOpen, setIsResourceDetailsOpen] = useState(false);

  const limit = 10;

  // ============================================
  // DETECT RESOURCE TYPE FUNCTION
  // ============================================
  const detectResourceType = (resource) => {
    if (resource.resource_type === 'ebook') return 'ebook';
    if (resource.resource_type === 'video') return 'video';
    if (resource.type_label === 'eBook' || resource.type_label === 'Ebook') return 'ebook';
    if (resource.type_label === 'Video') return 'video';
    
    if (resource.file_url && resource.file_url !== 'null') {
      const ext = resource.file_url.split('.').pop()?.toLowerCase();
      if (['pdf'].includes(ext)) return 'ebook';
      if (['doc', 'docx'].includes(ext)) return 'ebook';
      if (['mp4', 'webm', 'ogg'].includes(ext)) return 'video';
    }
    
    return resource.resource_type || 'ebook';
  };

  // ============================================
  // GET TYPE FUNCTIONS
  // ============================================
  const getTypeIcon = (type) => {
    if (type === 'ebook') return FileText;
    if (type === 'video') return Video;
    return FileText;
  };

  const getTypeColor = (type) => {
    if (type === 'ebook') return 'text-red-500';
    if (type === 'video') return 'text-green-500';
    return 'text-gray-500';
  };

  const getTypeBg = (type) => {
    if (type === 'ebook') return 'bg-red-50';
    if (type === 'video') return 'bg-green-50';
    return 'bg-gray-50';
  };

  const getTypeLabel = (type) => {
    if (type === 'ebook') return 'eBook';
    if (type === 'video') return 'Video';
    return type;
  };

  // ============================================
  // FETCH RESOURCES
  // ============================================
  const fetchResources = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: currentPage,
        limit: limit,
        search: searchQuery || undefined,
        category: selectedCategory || undefined,
        type: selectedType || undefined,
        sort: selectedSort || undefined,
      };

      Object.keys(params).forEach(key => {
        if (params[key] === undefined) delete params[key];
      });

      const response = await getMyResourcesLibrary(params);
      if (response.success && response.data) {
        const { data } = response;
        setCategories(data.categories || []);
        setAuthors(data.expert_authors || []);
        setTopics(data.popular_topics || []);
        setStats(data.stats || {});

        let resourcesData = [];
        
        if (data.trending && data.trending.length > 0) {
          resourcesData = data.trending;
        }
                if (data.resources && data.resources.length > 0) {
          const existingIds = new Set(resourcesData.map(r => r.id));
          data.resources.forEach(r => {
            if (!existingIds.has(r.id)) {
              resourcesData.push(r);
            }
          });
        }

        setTotalResources(resourcesData.length);

        const processedResources = resourcesData.map(item => {
          const detectedType = detectResourceType(item);
          
          let fileUrl = item.file_url;
          if (detectedType === 'ebook' && (!fileUrl || fileUrl === 'null')) {
            fileUrl = item.view_url || item.download_url || null;
          }
          
          return {
            id: item.id,
            title: item.title,
            type: getTypeLabel(detectedType),
            resource_type: detectedType,
            author: item.author?.name || item.author,
            time: item.duration_formatted || item.duration,
            rating: item.rating,
            icon: getTypeIcon(detectedType),
            color: getTypeColor(detectedType),
            bg: getTypeBg(detectedType),
            fullData: {
              ...item,
              resource_type: detectedType,
              type_label: getTypeLabel(detectedType),
              file_url: fileUrl,
            },
          };
        });

        const featured = resourcesData.filter(item => item.is_featured);
        setFeaturedResources(featured);
        
        setAllResources(processedResources);
        setTrending(data.trending || []);

        if (data.pagination) {
          setTotalPages(data.pagination.totalPages);
          setTotalResources(data.pagination.total || resourcesData.length);
        }

      } else {
        setError(response.message || "Failed to fetch resources");
      }
    } catch (err) {
      console.error(' Error fetching resources:', err);
      setError(err.message || "An error occurred while fetching resources");
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, selectedCategory, selectedType, selectedSort]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchResources();
      }
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);


  // HANDLE RESOURCE CLICK
  // ============================================
  const handleResourceClick = useCallback((resource) => {
    const fullData = resource.fullData || resource;
    
    const resourceWithType = {
      ...fullData,
      resource_type: fullData.resource_type || detectResourceType(fullData),
      type_label: fullData.type_label || getTypeLabel(fullData.resource_type || detectResourceType(fullData)),
    };
    
    setSelectedResource(resourceWithType);
    setIsResourceDetailsOpen(true);
  }, []);

  const handleCloseResourceDetails = useCallback(() => {
    setIsResourceDetailsOpen(false);
    setSelectedResource(null);
  }, []);

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1);
    switch (filterType) {
      case 'category': setSelectedCategory(value); break;
      case 'type': setSelectedType(value); break;
      case 'sort': setSelectedSort(value); break;
      default: break;
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const toggleViewMode = (mode) => {
    setViewMode(mode);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleFilterButtonClick = (filter) => {
    setActiveFilter(filter);
    setCurrentPage(1);

    if (filter === "All") {
      setSelectedCategory("");
      setSelectedType("");
    } else if (filter === "eBooks") {
      setSelectedType("ebook");
    } else if (filter === "Research Papers") {
      setSelectedType("research_paper");
    } else if (filter === "Guides") {
      setSelectedType("guide");
    } else if (filter === "Case Studies") {
      setSelectedType("case_study");
    } else if (filter === "Articles") {
      setSelectedType("article");
    }
  };

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  const formatType = (type) => {
    const types = {
      'ebook': 'eBook',
      'video': 'Video',
      'article': 'Article',
      'guide': 'Guide',
      'template': 'Template',
      'tool': 'Tool',
      'course': 'Course',
      'other': 'Other',
      'attachment': 'Attachment',
      'research_paper': 'Research Paper',
      'case_study': 'Case Study',
      'infographic': 'Infographic',
      'checklist': 'Checklist',
      'research': 'Research'
    };
    return types[type] || type;
  };

  const getCategoryIcon = (categoryName) => {
    const icons = {
      "Nutrition": Leaf,
      "Health": Heart,
      "Fitness": Dumbbell,
      "Wellness": Activity,
      "Diet": BookOpen,
      "Exercise": Dumbbell,
      "Mental Health": Heart,
      "General": BookOpen
    };
    return icons[categoryName] || BookOpen;
  };

  const getCategoryColor = () => 'text-green-500';
  const getCategoryBg = () => 'bg-green-50';

  const filterButtons = [
    "All",
    "eBooks",
    "Research Papers",
    "Guides",
    "Case Studies",
    "Articles",
  ];

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading && allResources.length === 0) {
    return (
      <div className="min-h-screen bg-[#f7f8f7] p-4 md:p-6">
        <div className="mx-auto">
          <div className="animate-pulse">
            <div className="h-48 sm:h-64 bg-gray-200 rounded-3xl mb-6"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-80 bg-gray-200 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && allResources.length === 0) {
    return (
      <div className="min-h-screen bg-[#f7f8f7] p-4 md:p-6 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl p-8 border border-gray-200">
          <div className="text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-gray-700">Failed to load resources</h3>
          <p className="text-gray-500 mt-2">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-[#f7f8f7] overflow-x-hidden pb-20 lg:pb-0">
      <div className="mx-auto">
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_290px] gap-4 md:gap-6">
          <div className="w-full min-w-0">
            {/* Hero Banner */}
            <div className="w-full rounded-2xl md:rounded-3xl overflow-hidden bg-gradient-to-r from-[#0d1f18] via-[#123c2d] to-[#22b36c] p-4 sm:p-6 md:p-8 text-white">
              <div className="flex flex-col lg:flex-row justify-between items-center gap-6 lg:gap-8">
                <div className="w-full text-center lg:text-left">
                  <span className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 text-xs sm:text-sm inline-block mb-3 sm:mb-4">
                    Member-Only Digital Library
                  </span>
                  <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight">
                    Explore Expert Knowledge & <br className="hidden sm:block" />
                    Research Publications
                  </h1>
                  <p className="mt-2 sm:mt-3 md:mt-4 text-green-100 text-xs sm:text-sm md:text-base max-w-2xl mx-auto lg:mx-0">
                    Access curated eBooks, research papers, nutrition guides, case studies, and expert
                    publications exclusively for enrolled members.
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 flex-shrink-0">
                  <div className="text-center">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">{stats.total_resources || totalResources || 0}+</h2>
                    <p className="text-green-100 mt-1 text-[10px] sm:text-xs md:text-sm">Resources</p>
                  </div>
                  <div className="text-center">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">{stats.total_authors || authors.length || 0}</h2>
                    <p className="text-green-100 mt-1 text-[10px] sm:text-xs md:text-sm">Expert Authors</p>
                  </div>
                  <div className="text-center">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">{stats.total_categories || categories.length || 0}</h2>
                    <p className="text-green-100 mt-1 text-[10px] sm:text-xs md:text-sm">Categories</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Search & Filters */}
            <div className="mt-4 md:mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-between items-stretch sm:items-center">
              <div className="relative w-full sm:max-w-[280px] md:max-w-[340px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  placeholder="Search eBooks, guides..."
                  className="w-full h-10 sm:h-11 md:h-12 pl-9 pr-3 bg-white border border-gray-200 rounded-xl outline-none text-xs sm:text-sm md:text-base focus:border-green-500 focus:ring-2 focus:ring-green-200 transition"
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </div>
              <div className="flex flex-wrap justify-center sm:justify-end gap-1.5 sm:gap-2">
                {filterButtons.map((item) => (
                  <button
                    key={item}
                    onClick={() => handleFilterButtonClick(item)}
                    className={`h-8 sm:h-9 md:h-10 px-2.5 sm:px-3 md:px-5 rounded-full text-[10px] sm:text-xs md:text-sm whitespace-nowrap transition ${
                      activeFilter === item ? "bg-green-600 text-white" : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {/* Category Chips */}
            <div className="flex gap-1.5 sm:gap-2 md:gap-3 mt-3 sm:mt-4 md:mt-6 overflow-x-auto pb-2 scrollbar-hide">
              {categories.slice(0, 5).map((item) => {
                const Icon = getCategoryIcon(item.name);
                return (
                  <button
                    key={item.name}
                    onClick={() => handleFilterChange('category', selectedCategory === item.name ? "" : item.name)}
                    className={`flex items-center gap-1 sm:gap-1.5 md:gap-2 h-9 sm:h-10 md:h-11 px-2.5 sm:px-3 md:px-5 rounded-xl whitespace-nowrap text-[10px] sm:text-xs md:text-sm flex-shrink-0 transition ${
                      selectedCategory === item.name ? "bg-[#20312b] text-white" : "bg-white border hover:bg-gray-50"
                    }`}
                  >
                    <Icon size={14} className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    <span className="hidden xs:inline">{item.name}</span>
                    <span className="xs:hidden">{item.name.split(" ")[0]}</span>
                    <span className={`text-[10px] ${selectedCategory === item.name ? "text-green-200" : "text-gray-400"}`}>
                      {item.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Featured Resources */}
            <div className="mt-6 sm:mt-8">
              <div className="flex items-center justify-between mb-4 sm:mb-5">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-[#23352F]">
                  Featured Resources
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {featuredResources.length > 0 ? (
                  featuredResources.map((item) => {
                    const detectedType = detectResourceType(item);
                    const Icon = getTypeIcon(detectedType);
                    const color = getTypeColor(detectedType);
                    const bg = getTypeBg(detectedType);
                    const typeLabel = getTypeLabel(detectedType);

                    return (
                      <div
                        key={item.id}
                        className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition duration-300 cursor-pointer"
                        onClick={() => handleResourceClick({ ...item, fullData: { ...item, resource_type: detectedType, type_label: typeLabel } })}
                      >
                        <div className={`relative h-40 sm:h-44 md:h-48 flex items-center justify-center ${bg}`}>
                          <div className="text-center">
                            <Icon className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 ${color} mx-auto`} />
                          </div>

                          <span className={`absolute top-3 left-3 text-white text-[10px] sm:text-xs px-2.5 sm:px-3 py-1 rounded-full font-medium ${detectedType === 'ebook' ? 'bg-red-500' : 'bg-green-500'}`}>
                            {typeLabel}
                          </span>

                          {item.is_featured && (
                            <span className="absolute top-3 right-3 bg-yellow-400 text-white text-[10px] sm:text-xs px-2.5 py-1 rounded-full font-medium">
                              Featured
                            </span>
                          )}
                        </div>

                        <div className="p-3 sm:p-4">
                          <h3 className="font-bold text-sm sm:text-base md:text-lg text-[#23352F] leading-5 sm:leading-6 min-h-[40px] sm:min-h-[48px] line-clamp-2">
                            {item.title}
                          </h3>

                          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 mt-2 sm:mt-3">
                            <img
                              src={item.author?.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.author?.name || 'User')}&background=0f6f45&color=fff&size=40`}
                              className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover"
                              alt="author"
                              onError={(e) => {
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(item.author?.name || 'User')}&background=0f6f45&color=fff&size=40`;
                              }}
                            />
                            <span className="truncate">{item.author?.name || item.author}</span>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] sm:text-xs md:text-sm text-gray-400 mt-2 sm:mt-3">
                            <span className="flex items-center gap-1">
                              <Clock3 size={13} className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                              {item.duration_formatted || item.duration || "N/A"}
                            </span>
                            <span className="hidden xs:inline">{item.views || 0} views</span>
                            <span className="flex items-center gap-1 text-orange-400">
                              <Star size={13} className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                              {item.rating || "0.00"}
                            </span>
                          </div>

                          {item.tags && item.tags.length > 0 && (
                            <div className="flex gap-1.5 sm:gap-2 mt-2 sm:mt-3 flex-wrap">
                              {item.tags.slice(0, 2).map((tag) => (
                                <span
                                  key={tag}
                                  className="bg-green-50 text-green-700 px-2 sm:px-3 py-0.5 rounded-full text-[9px] sm:text-[10px] md:text-xs"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="mt-3 sm:mt-4">
                            <button 
                              className="w-full bg-green-600 hover:bg-green-700 text-white px-2.5 sm:px-4 md:px-5 py-1.5 sm:py-2 rounded-lg flex items-center justify-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs md:text-sm font-medium transition"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResourceClick({ ...item, fullData: { ...item, resource_type: detectedType, type_label: typeLabel } });
                              }}
                            >
                              <Eye size={14} className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              {detectedType === 'ebook' ? 'Read eBook' : 'Watch Video'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-full text-center text-gray-500 py-8">
                    No featured resources available
                  </div>
                )}
              </div>
            </div>

            {/* All Resources */}
            <div className="mt-8 sm:mt-10">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-5 gap-3 sm:gap-0">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-[#23352F]">
                  All Resources ({totalResources || allResources.length})
                </h2>
                <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                  <select
                    className="text-xs sm:text-sm text-gray-400 bg-transparent border-none outline-none cursor-pointer hover:text-gray-600 transition"
                    value={selectedSort}
                    onChange={(e) => handleFilterChange('sort', e.target.value)}
                  >
                    <option value="">Sort: Default</option>
                    <option value="created_at">Newest First</option>
                    <option value="title">Title (A-Z)</option>
                    <option value="title_desc">Title (Z-A)</option>
                    <option value="downloads">Most Downloads</option>
                    <option value="views">Most Views</option>
                    <option value="rating">Highest Rated</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2.5 sm:space-y-3">
                {allResources.length > 0 ? (
                  allResources.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.id}
                        className="bg-white border border-gray-200 rounded-2xl px-3 sm:px-4 md:px-5 py-3 sm:py-4 hover:shadow-sm transition-all duration-300 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 cursor-pointer"
                        onClick={() => handleResourceClick(item)}
                      >
                        <div className="flex items-center gap-3 sm:gap-4 flex-1 w-full sm:w-auto">
                          <div
                            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${item.bg}`}
                          >
                            <Icon className={item.color} size={20} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm sm:text-base md:text-[18px] text-[#23352F] line-clamp-2">
                              {item.title}
                            </h3>
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 md:gap-3 mt-1 text-[10px] sm:text-xs md:text-sm text-gray-400">
                              <span
                                className={`px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] md:text-xs font-medium ${item.bg} ${item.color}`}
                              >
                                {item.type}
                              </span>
                              <span className="flex items-center gap-0.5 sm:gap-1">
                                <User size={12} className="w-3 h-3" />
                                <span className="truncate max-w-[80px] sm:max-w-[120px]">{item.author}</span>
                              </span>
                              <span className="flex items-center gap-0.5 sm:gap-1">
                                <Clock3 size={12} className="w-3 h-3" />
                                {item.time || "N/A"}
                              </span>
                              {/* <span className="flex items-center gap-0.5 sm:gap-1 text-orange-400">
                                <Star size={12} className="w-3 h-3" />
                                {item.rating || "0.00"}
                              </span> */}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 w-full sm:w-auto justify-start sm:justify-end">
                          <button 
                            className={`px-3 sm:px-4 md:px-5 h-8 sm:h-9 md:h-10 rounded-xl font-semibold flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs md:text-sm transition ${
                              item.resource_type === 'ebook' 
                                ? 'bg-red-50 hover:bg-red-100 text-red-600' 
                                : 'bg-green-50 hover:bg-green-100 text-green-600'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResourceClick(item);
                            }}
                          >
                            <Eye size={14} className="w-3.5 h-3.5" />
                            {item.resource_type === 'ebook' ? 'Read' : 'Watch'}
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    {loading ? "Loading resources..." : "No resources found"}
                  </div>
                )}
              </div>

              {totalPages > 1 && (
                <div className="flex flex-wrap justify-center gap-2 mt-6">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white border border-gray-200 hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    Previous
                  </button>
                  <span className="flex items-center px-4 py-2 rounded-xl bg-green-50 text-green-600 font-medium text-sm">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                      currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white border border-gray-200 hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="hidden xl:block space-y-6">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-lg font-bold text-gray-800 mb-5">
                Browse Categories
              </h3>
              <div className="space-y-3">
                {categories.map((item) => {
                  const Icon = getCategoryIcon(item.name);
                  return (
                    <button
                      key={item.name}
                      onClick={() => handleFilterChange('category', selectedCategory === item.name ? "" : item.name)}
                      className={`w-full flex items-center justify-between rounded-2xl px-2 py-2 transition-all duration-300 ${
                        selectedCategory === item.name ? "bg-green-50" : "bg-gray-50 hover:bg-green-50"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center ${getCategoryBg(item.name)}`}
                        >
                          <Icon className={getCategoryColor(item.name)} size={20} />
                        </div>
                        <span className={`font-medium ${selectedCategory === item.name ? "text-green-700" : "text-gray-800"}`}>
                          {item.name}
                        </span>
                      </div>
                      <span className={`font-semibold ${selectedCategory === item.name ? "text-green-500" : "text-gray-400"}`}>
                        {item.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-lg font-bold text-[#23352F] mb-5">
                Trending Now
              </h3>
              <div className="space-y-3">
                {trending.slice(0, 5).map((item, index) => (
                  <div
                    key={item.id}
                    className="bg-[#f8f9f7] hover:bg-green-50 rounded-2xl px-4 py-4 flex items-center justify-between transition duration-300 cursor-pointer"
                    onClick={() => handleResourceClick(item)}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-3xl font-bold text-gray-200 w-9 flex-shrink-0">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-[#23352F] leading-6 line-clamp-2">
                          {item.title}
                        </h4>
                        <p className="text-gray-400 text-xs mt-1">
                          {item.type_label || formatType(item.resource_type)}
                          <span className="mx-1">·</span>
                          {item.views || 0} views
                        </p>
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-gray-400 flex-shrink-0 ml-2" />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-[#23352F]">
                  Expert Authors
                </h3>
              </div>
              <div className="space-y-3">
                {authors.slice(0, 4).map((author) => (
                  <div
                    key={author.name}
                    className="bg-[#F8F9F7] rounded-2xl px-4 py-4 flex items-center justify-between hover:bg-green-50 transition-all duration-300"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={author.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(author.name)}&background=0f6f45&color=fff&size=60`}
                        alt={author.name}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(author.name)}&background=0f6f45&color=fff&size=60`;
                        }}
                      />
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-[#23352F] leading-5 truncate">
                          {author.name}
                        </h4>
                        <p className="text-xs text-gray-400 leading-5 truncate">
                          {author.designation}
                        </p>
                        <p className="text-xs text-gray-400 leading-5">
                          {author.total_courses || 0} publications
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-gray-200 p-5">
              <h3 className="font-bold text-lg mb-5">Popular Topics</h3>
              <div className="flex flex-wrap gap-2">
                {topics.length > 0 ? (
                  topics.map((topic) => (
                    <span
                      key={topic}
                      className="px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-sm hover:bg-green-100 transition cursor-pointer"
                      onClick={() => handleFilterChange('category', selectedCategory === topic ? "" : topic)}
                    >
                      {topic}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-400 text-sm">No topics available</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-3 py-2.5 flex justify-around items-center shadow-lg z-50">
        <button className="flex flex-col items-center text-green-600">
          <BookOpen size={20} className="w-5 h-5" />
          <span className="text-[9px] mt-0.5">Library</span>
        </button>
        <button className="flex flex-col items-center text-gray-400 hover:text-green-600 transition">
          <Search size={20} className="w-5 h-5" />
          <span className="text-[9px] mt-0.5">Search</span>
        </button>
        <button className="flex flex-col items-center text-gray-400 hover:text-green-600 transition">
          <Bookmark size={20} className="w-5 h-5" />
          <span className="text-[9px] mt-0.5">Saved</span>
        </button>
        <button className="flex flex-col items-center text-gray-400 hover:text-green-600 transition">
          <User size={20} className="w-5 h-5" />
          <span className="text-[9px] mt-0.5">Profile</span>
        </button>
      </div>

      <div className="lg:hidden h-16"></div>

      {/* Resource Detail Popup */}
      {isResourceDetailsOpen && selectedResource && (
        <ResourceDetail
          resource={selectedResource}
          onClose={handleCloseResourceDetails}
          onNext={() => {}}
          onPrevious={() => {}}
          hasNext={false}
          hasPrevious={false}
        />
      )}
    </div>
  );
}